// Shared token-refresh single-flight.
//
// There is exactly ONE in-flight refresh across the whole app. Both API layers —
// `HttpClient` (Layer A) and `modules/apiUtils` (Layer B) — call `refreshAuthToken()` here
// instead of each keeping their own `refreshTokenRequest`. This removes the cross-layer
// rotation race: the server rotates (invalidates) the refresh token on every refresh, so two
// concurrent refreshes (e.g. WeeklyCalendar via Layer A + Calendar/Map via Layer B) used to
// spend the same refresh token, invalidating the second and forcing a logout.
//
// This module ONLY refreshes + stores the new tokens and throws on failure. It does NOT clear
// the session or redirect — each caller keeps its own failure handling (HttpClient redirects,
// apiUtils throws), preserving existing behavior.

import { AppConstant } from "../config/AppConstants";
import { BASE_URL, GetRefreshToken_api } from "../config/ApiConfig";
import {
  getRefreshToken,
  getGuId,
  setAccessToken,
  setRefreshToken,
} from "../utils/SecurityService";

let refreshTokenRequest = null;

const doRefresh = async () => {
  const refreshResponse = await fetch(BASE_URL + GetRefreshToken_api, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      AppVersion: AppConstant.AppVersion,
      RefreshToken: getRefreshToken(),
      Language: AppConstant.Language,
      DeviceId: AppConstant.DeviceID,
      OsVersion: AppConstant.OsVersion,
      ClientId: AppConstant.DEFAULT_CLIENT_ID,
      GuId: getGuId(),
      ManufactureModel: AppConstant.ManufactureModel,
      GrantType: "User",
    }),
  });

  if (!refreshResponse.ok) {
    throw new Error("Failed to refresh token");
  }

  const refreshData = await refreshResponse.json();
  const { AccessToken, RefreshToken } = refreshData.Data.Records;

  setAccessToken(AccessToken);
  setRefreshToken(RefreshToken);

  return { accessToken: AccessToken, refreshToken: RefreshToken };
};

// Returns the single shared in-flight refresh promise, creating it if none is running.
// Concurrent callers (within or across API layers) all await the same refresh.
export const refreshAuthToken = () => {
  if (!refreshTokenRequest) {
    refreshTokenRequest = doRefresh().finally(() => {
      refreshTokenRequest = null;
    });
  }
  return refreshTokenRequest;
};
